import { format } from "path";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { useShallow } from "zustand/react/shallow";

export interface imagesName {
  frontView: File | null;
  backView: File | null;
  leftView: File | null;
  rightView: File | null;
  livingRoom: File | null;
  kitchen: File | null;
  bedroom: File | null;
  bathroom: File | null;
  diningRoom: File | null;
  veranda: File | null;
  googleEarthOne: File | null;
  googleEarthTwo: File | null;
  masterPlan: File | null;
}

export interface ValuationFormValues extends imagesName {
  id?: string;

  // ─── Step 1 · Property Identity ───────────────────────────────────────────
  upi: string;
  plotNumber: string;
  ownerIdNumber: string;
  ownerName: string;

  // ─── Location · Rwanda ────────────────────────────────────────────────────
  country: string;
  province: string;
  district: string;
  sector: string;
  cell: string;
  village: string;

  // ─── Location · Uganda ────────────────────────────────────────────────────
  region: string;        // Administrative Region (Central, Eastern, Northern, Western)
  subRegion: string;     // Sub-region (Karamoja, Buganda, Busoga, etc.)
  county: string;        // County within district
  subcounty: string;     // Sub-county (maps to sector in Rwanda)
  parish: string;        // Parish (maps to cell in Rwanda)
  constituency: string;  // Electoral constituency

  // ─── Location · Shared ────────────────────────────────────────────────────    // ISO country code or full name (e.g. "uganda", "RW")
  streetAddress: string;
  latitude: number;
  longitude: number;

  // ─── Valuation Task Meta ──────────────────────────────────────────────────
  institutionalId?: string;
  valuationAssignmentDate?: string;
  professionalCert?: string;
  principalValuer?: string;
  assistantValuer?: string;
  regulatoryBody?: string;

  // ─── Workflow State ───────────────────────────────────────────────────────
  step?: number;
  estimatedDuration?: number;
  timeRemaining?: number | null;
  valuationStatus?: string;        // "New" | "In Progress" | "Completed" | etc.
  status?: string;                 // "draft" | "submitted" | "approved" | etc.

  // ─── Step 1 · Property Details ────────────────────────────────────────────
  dateOfVisit: string;
  propertyName: string;
  valuationPurpose: string;
  propertyType: string;

  // ─── Land Parcel ──────────────────────────────────────────────────────────
  landArea: number;
  slopeCategory: string;
  landUse: string;
  landValue: number;
  landRate: number;
  accessToRoad: boolean;
  zoning: string;
  planningPermit?: string | null;
  tenure?: string | null;

  // ─── Main House ───────────────────────────────────────────────────────────
  mainHouseArea: number;
  mainHouseValue: number;
  mainHouseRate: number;
  mainHouseCondition: string;
  mainHouseFloors: number;

  // ─── Annex Building ───────────────────────────────────────────────────────
  annexHouseArea: number;
  annexHouseValue: number;
  annexRate: number;
  annexHouseCondition: string;
  annexHouseFloors: number;

  // ─── Fence ────────────────────────────────────────────────────────────────
  fenceLength: number;
  fenceRate: number;
  fenceValue: number;
  fenceCondition: string;
  hasFence?: boolean;
  gateFenceLength: number;

  // ─── Retaining Walls ──────────────────────────────────────────────────────
  retainingWallsLength: number;
  retainingWallRate: number;
  retainingWallsValue: number;
  retainingWallsCondition: string;
  hasRetainingWalls?: boolean;

  // ─── Entrance Gate ────────────────────────────────────────────────────────
  entranceGateLength: number;
  entranceGateRate: number;
  entranceGateValue: number;
  entranceGateCondition: string;

  // ─── Paved Area / Parking ─────────────────────────────────────────────────
  pavedArea: number;
  pavedAreaRate: number;
  pavedAreaValue: number;
  pavedAreaCondition: string;

  // ─── Septic Tank ──────────────────────────────────────────────────────────
  septicTankNumber: number;
  septicTankValue: number;
  septicTanksValue: number;
  septicTanksCondition: string;

  // ─── Garden ───────────────────────────────────────────────────────────────
  gardenArea: number;
  gardenRate: number;
  gardenValue: number;
  gardenCondition: string;

  // ─── Verandah ─────────────────────────────────────────────────────────────
  verandahArea: number;
  verandahRate: number;
  verandahValue: number;
  verandahCondition: string;
  hasVeranda?: boolean;

  // ─── Landscaping ──────────────────────────────────────────────────────────
  landscapingValue: number;

  // ─── Adjustments ──────────────────────────────────────────────────────────
  depreciationRate: number;
  forcedSaleDiscount: number;

  // ─── Summary / Calculated Values ─────────────────────────────────────────
  totalPropertyValue: number;
  totalPropertyValueAfterForcedSale: number;
  totalPropertyValueAfterDepreciation: number;
  buildingValue?: number;
  amenitiesValue?: number;
  depreciationValue?: number;
  improvementsValue?: number;
  openMarketValue?: number;
  forcedSaleValue?: number;
  insuranceValue?: number;

  // ─── Valuation Dates & Approvals ─────────────────────────────────────────
  valuationDate?: string;
  collectorNotes?: string;
  approvedBy?: string;
  approvedDate?: string;
  assignedCollector?: string;
  assignedInstitutional?: string;

  // ─── Step 2 · Building Details ────────────────────────────────────────────
  constructedArea: number;
  yearOfConstruction: number;
  numberOfFloors?: number;
  constructionQuality?: string;
  exteriorFinish?: string;
  roofType: "Iron Sheets" | "Tiles" | "Concrete" | "Thatch";
  maintenanceCondition?: number;
  bedrooms: number;
  bathrooms: number;

  // Condition ratings
  roofCondition:    "Excellent" | "Good" | "Average" | "Poor" | "Needs Repair";
  wallsCondition:   "Excellent" | "Good" | "Average" | "Poor" | "Needs Repair";
  windowsCondition: "Excellent" | "Good" | "Average" | "Poor" | "Needs Repair";
  doorsCondition:   "Excellent" | "Good" | "Average" | "Poor" | "Needs Repair";
  floorCondition:   "Excellent" | "Good" | "Average" | "Poor" | "Needs Repair";
  wallCondition: string;

  // Cost approach / depreciation
  effectiveAge: number;
  annualDepreciationRate: number;
  unitPrice: number;
  originalCostOfConstruction: number;
  maintenanceCoefficient: number;
  depreciatedValue: number;
  presentValue: number;

  // ─── Step 2 · Amenities & Boolean Flags ──────────────────────────────────
  hasAnnexBuilding: boolean;
  hasWater: boolean;
  hasWaterTank?: boolean;
  hasElectricity: boolean;
  hasOutsideKitchen: boolean;
  hasGarden: boolean;
  hasPavedParking: boolean;
  hasSewage?: boolean;
  hasInternet?: boolean;
  hasParkingSpace?: boolean;

  // ─── Step 2 · Component Descriptions ─────────────────────────────────────
  outsideKitchenDescription: string;
  waterTankDescription: string;
  landscapingDescription: string;
  soilDrainageDescription: string;
  pvcPipesDescription: string;
  washTapsDescription: string;
  fixturesFittingsDescription: string;

  // ─── Step 3 · Documents & Maps ───────────────────────────────────────────
  googleEarth: string | null;
  cityMasterPlan: string | null;
  photo: File | null;

  // ─── Step 3 · Misc ────────────────────────────────────────────────────────
  area: number;
  age: number;
  condition: number;

  // ─── Step 3 · Remarks ────────────────────────────────────────────────────
  remarks: string;
  recommendations: string;
  additionalNotes: string;

  // ─── Media Collections ───────────────────────────────────────────────────
  photos: string[];
  documents: string[];

  // ─── Audit & Relations ───────────────────────────────────────────────────
  createdAt?: string;
  updatedAt?: string;
  phone?: string;
  viewerOwnerId?: string;
  collectorId?: string | null;
  viewerCollectorId?: string | null;
  supervisorId?: string | null;
  viewerSupervisorId?: string | null;
  certifiedAt?: string | null;
  confidenceLevel?: number | null;
}

interface ValuationFormStore {
  // Current step
  currentStep: number;

  // Form data
  formData: ValuationFormValues;

  // Step management
  setCurrentStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;

  // Form data management
  updateFormData: (data: Partial<Record<keyof ValuationFormValues, any>>) => void;

  updateStep1Data: (
    data: Partial<
      Pick<
        ValuationFormValues,
        | "upi"
        | "plotNumber"
        | "ownerIdNumber"
        | "ownerName"
        | "country"
        | "region"
        | "subRegion"
        | "county"
        | "village"
        | "streetAddress"
        | "latitude"
        | "longitude"
        | "landArea"
        | "slopeCategory"
        | "landUse"
        | "landValue"
        | "landRate"
        | "mainHouseArea"
        | "mainHouseRate"
        | "annexHouseArea"
        | "annexRate"
        | "fenceLength"
        | "fenceRate"
        | "retainingWallsLength"
        | "retainingWallRate"
        | "entranceGateLength"
        | "entranceGateRate"
        | "pavedArea"
        | "pavedAreaRate"
        | "septicTankNumber"
        | "septicTankValue"
        | "gardenArea"
        | "gardenRate"
        | "verandahArea"
        | "verandahRate"
        | "landscapingValue"
        | "depreciationRate"
        | "forcedSaleDiscount"
      >
    >
  ) => void;

  updateStep2Data: (
    data: Partial<
      Pick<
        ValuationFormValues,
        | "hasAnnexBuilding"
        | "hasOutsideKitchen"
        | "hasGarden"
        | "hasPavedParking"
        | "accessToRoad"
        | "outsideKitchenDescription"
        | "gateFenceLength"
        | "waterTankDescription"
        | "landscapingDescription"
        | "soilDrainageDescription"
        | "pvcPipesDescription"
        | "washTapsDescription"
        | "roofCondition"
        | "wallsCondition"
        | "windowsCondition"
        | "doorsCondition"
        | "floorCondition"
        | "fixturesFittingsDescription"
        | "bedrooms"
        | "bathrooms"
        | "yearOfConstruction"
        | "effectiveAge"
        | "annualDepreciationRate"
        | "unitPrice"
        | "constructedArea"
        | "originalCostOfConstruction"
        | "maintenanceCoefficient"
        | "depreciatedValue"
        | "presentValue"
        | "roofType"
        | "hasWater"
        | "hasElectricity"
      >
    >
  ) => void;

  updateStep3Data: (
    data: Partial<
      Pick<
        ValuationFormValues,
        "photo" | "area" | "age" | "condition" | "zoning"
      >
    >
  ) => void;

  // Utility methods
  resetForm: () => void;
  isStepValid: (step: number) => boolean;
  getStepData: (step: number) => Partial<ValuationFormValues>;
}

// Default form values
const defaultFormData: ValuationFormValues = {
  // Step 1
  upi: "",
  plotNumber: "",
  ownerIdNumber: "",
  ownerName: "",
  country: "",
  province: "",
  district: "",
  sector: "",
  cell: "",
  region: "",
  subRegion: "",
  county: "",
  subcounty: "",
  parish: "",
  constituency: "",
  village: "",
  streetAddress: "",
  latitude: 0,
  longitude: 0,
  landArea: 0,
  slopeCategory: "",
  landUse: "",
  landValue: 0,
  landRate: 0,
  mainHouseArea: 0,
  mainHouseRate: 0,
  annexHouseArea: 0,
  mainHouseValue: 0,
  annexHouseValue: 0,
  fenceValue: 0,
  retainingWallsValue: 0,
  entranceGateValue: 0,
  pavedAreaValue: 0,
  gardenValue: 0,
  verandahValue: 0,
  septicTanksValue: 0,
  totalPropertyValue: 0,
  totalPropertyValueAfterForcedSale: 0,
  totalPropertyValueAfterDepreciation: 0,
  annexRate: 0,
  fenceLength: 0,
  fenceRate: 0,
  retainingWallsLength: 0,
  retainingWallRate: 0,
  entranceGateLength: 0,
  entranceGateRate: 0,
  pavedArea: 0,
  pavedAreaRate: 0,
  septicTankNumber: 0,
  septicTankValue: 0,
  gardenArea: 0,
  gardenRate: 0,
  verandahArea: 0,
  verandahRate: 0,
  landscapingValue: 0,
  depreciationRate: 0,
  forcedSaleDiscount: 0,
  dateOfVisit: "",
  propertyName: "",
  valuationPurpose: "",
  propertyType: "",


  // Step 2
  hasAnnexBuilding: false,
  hasWater: false,
  hasElectricity: false,
  hasOutsideKitchen: false,
  hasGarden: false,
  hasPavedParking: false,
  accessToRoad: false,
  outsideKitchenDescription: "",
  gateFenceLength: 0,
  waterTankDescription: "",
  landscapingDescription: "",
  soilDrainageDescription: "",
  pvcPipesDescription: "",
  washTapsDescription: "",
  roofCondition: "Excellent",
  wallsCondition: "Excellent",
  windowsCondition: "Excellent",
  doorsCondition: "Excellent",
  floorCondition: "Excellent",
  fixturesFittingsDescription: "",
  mainHouseFloors: 0,
  annexHouseCondition: "",
  annexHouseFloors: 0,
  mainHouseCondition: "",
  wallCondition: "",
  fenceCondition: "",
  retainingWallsCondition: "",
  entranceGateCondition: "",
  bedrooms: 0,
  bathrooms: 0,
  yearOfConstruction: new Date().getFullYear(),
  effectiveAge: 0,
  annualDepreciationRate: 0,
  unitPrice: 0,
  constructedArea: 0,
  originalCostOfConstruction: 0,
  maintenanceCoefficient: 0,
  depreciatedValue: 0,
  presentValue: 0,
  roofType: "Iron Sheets",

  // Step 3
  googleEarth: null,
  cityMasterPlan: null,
  photo: null,
  area: 0,
  age: 0,
  condition: 0,
  zoning: "",
  frontView: null,
  backView: null,
  leftView: null,
  rightView: null,
  livingRoom: null,
  kitchen: null,
  bedroom: null,
  bathroom: null,
  diningRoom: null,
  veranda: null,
  googleEarthOne: null,
  googleEarthTwo: null,
  masterPlan: null,
  pavedAreaCondition: "",
  septicTanksCondition: "",
  gardenCondition: "",
  verandahCondition: "",
  remarks: "",
  recommendations: "",
  additionalNotes: "",
  photos: [],
  documents: [],
};

export const useValuationFormStore = create<ValuationFormStore>()(
  persist(
    (set, get) => ({
      currentStep: 1,
      formData: defaultFormData,

      // Step management
      setCurrentStep: (step: number) => {
        if (step >= 1 && step <= 3) {
          set({ currentStep: step });
        }
      },

      nextStep: () => {
        const { currentStep } = get();
        if (currentStep < 3) {
          set({ currentStep: currentStep + 1 });
        }
      },

      prevStep: () => {
        const { currentStep } = get();
        if (currentStep > 1) {
          set({ currentStep: currentStep - 1 });
        }
      },

      // Form data management
      updateFormData: (data: Partial<ValuationFormValues>) => {
        set((state) => ({
          formData: { ...state.formData, ...data },
        }));
      },

      updateStep1Data: (data) => {
        set((state) => ({
          formData: { ...state.formData, ...data },
        }));
      },

      updateStep2Data: (data) => {
        set((state) => ({
          formData: { ...state.formData, ...data },
        }));
      },

      updateStep3Data: (data) => {
        set((state) => ({
          formData: { ...state.formData, ...data },
        }));
      },

      // Utility methods
      resetForm: () => {
        set({
          currentStep: 1,
          formData: defaultFormData,
        });
      },

      isStepValid: (step: number) => {
        const { formData } = get();

        switch (step) {
          case 1:
            return !!(
              formData.upi &&
              formData.plotNumber &&
              formData.ownerIdNumber &&
              formData.ownerName &&
              formData.province &&
              formData.region &&
              formData.subRegion &&
              formData.county &&
              formData.district &&
              formData.sector &&
              formData.cell &&
              formData.village && 
              formData.streetAddress &&
              formData.latitude !== 0 &&
              formData.longitude !== 0 &&
              formData.landArea 

            );
          case 2:
            return !!(
              formData.bedrooms >= 0 &&
              formData.bathrooms >= 0 &&
              formData.yearOfConstruction > 0
            );
          case 3:
            return !!(
              formData.area > 0 &&
              formData.age >= 0 &&
              formData.zoning
            );
          default:
            return false;
        }
      },

      getStepData: (step: number) => {
        const { formData } = get();

        console.log(`Getting data for step ${step}:`, formData);

        switch (step) {
          case 1:
            return {
              upi: formData.upi,
              plotNumber: formData.plotNumber,
              ownerIdNumber: formData.ownerIdNumber,
              ownerName: formData.ownerName,
              country: formData.country,
              region: formData.region,
              subRegion: formData.subRegion,
              county: formData.county,
              province: formData.province,
              district: formData.district,
              sector: formData.sector,
              cell: formData.cell,
              village: formData.village,
              streetAddress: formData.streetAddress,
              latitude: formData.latitude,
              longitude: formData.longitude,
              landArea: formData.landArea,
              slopeCategory: formData.slopeCategory,
              landUse: formData.landUse,
              landValue: formData.landRate * formData.landArea,
              landRate: formData.landRate,
              mainHouseArea: formData.mainHouseArea,
              mainHouseRate: formData.mainHouseRate,
              annexHouseArea: formData.annexHouseArea,
              annexRate: formData.annexRate,
              fenceLength: formData.fenceLength,
              fenceRate: formData.fenceRate,
              retainingWallsLength: formData.retainingWallsLength,
              retainingWallRate: formData.retainingWallRate,
              entranceGateLength: formData.entranceGateLength,
              entranceGateRate: formData.entranceGateRate,
              pavedArea: formData.pavedArea,
              pavedAreaRate: formData.pavedAreaRate,
              septicTankNumber: formData.septicTankNumber,
              septicTankValue: formData.septicTankValue,
              gardenArea: formData.gardenArea,
              gardenRate: formData.gardenRate,
              verandahArea: formData.verandahArea,
              verandahRate: formData.verandahRate,
              landscapingValue: formData.landscapingValue,
              depreciationRate: formData.depreciationRate,
              forcedSaleDiscount: formData.forcedSaleDiscount,
            };
          case 2:
            return {
              hasAnnexBuilding: formData.hasAnnexBuilding,
              hasWater: formData.hasWater,
              hasElectricity: formData.hasElectricity,
              hasOutsideKitchen: formData.hasOutsideKitchen,
              hasGarden: formData.hasGarden,
              hasPavedParking: formData.hasPavedParking,
              accessToRoad: formData.accessToRoad,
              outsideKitchenDescription: formData.outsideKitchenDescription,
              gateFenceLength: formData.gateFenceLength,
              waterTankDescription: formData.waterTankDescription,
              landscapingDescription: formData.landscapingDescription,
              soilDrainageDescription: formData.soilDrainageDescription,
              pvcPipesDescription: formData.pvcPipesDescription,
              washTapsDescription: formData.washTapsDescription,
              roofCondition: formData.roofCondition,
              wallsCondition: formData.wallsCondition,
              windowsCondition: formData.windowsCondition,
              doorsCondition: formData.doorsCondition,
              floorCondition: formData.floorCondition,
              fixturesFittingsDescription: formData.fixturesFittingsDescription,
              bedrooms: formData.bedrooms,
              bathrooms: formData.bathrooms,
              yearOfConstruction: formData.yearOfConstruction,
              effectiveAge: formData.effectiveAge,
              annualDepreciationRate: formData.annualDepreciationRate,
              unitPrice: formData.unitPrice,
              constructedArea: formData.constructedArea,
              originalCostOfConstruction: formData.originalCostOfConstruction,
              maintenanceCoefficient: formData.maintenanceCoefficient,
              depreciatedValue: formData.depreciatedValue,
              presentValue: formData.presentValue,
              roofType: formData.roofType,
            };
          case 3:
            return {
              photo: formData.photo,
              area: formData.area,
              age: formData.age,
              condition: formData.condition,
              zoning: formData.zoning,
              frontView: formData.frontView,
              backView: formData.backView,
              leftView: formData.leftView,
              rightView: formData.rightView,
              livingRoom: formData.livingRoom,
              kitchen: formData.kitchen,
              bedroom: formData.bedroom,
              bathroom: formData.bathroom,
              diningRoom: formData.diningRoom,
              veranda: formData.veranda,
              googleEarthOne: formData.googleEarthOne,
              googleEarthTwo: formData.googleEarthTwo,
              masterPlan: formData.masterPlan,
            };
          default:
            return {};
        }
      },
    }),
    {
      name: "valuation-form-storage",
      // Exclude photos from persistence since File objects can't be serialized
      partialize: (state) => ({
        ...state,
        formData: {
          ...state.formData,
          photo: null,
          frontView: null,
          backView: null,
          leftView: null,
          rightView: null,
          livingRoom: null,
          kitchen: null,
          bedroom: null,
          bathroom: null,
          diningRoom: null,
          veranda: null,
          googleEarthOne: null,
          googleEarthTwo: null,
          masterPlan: null,
        },
      }),
    }
  )
);

// Helper hooks for easier usage
export const useCurrentStep = () =>
  useValuationFormStore((state) => state.currentStep);
export const useFormData = () =>
  useValuationFormStore((state) => state.formData);
export const useStepActions = () =>
  useValuationFormStore((state) => ({
    setCurrentStep: state.setCurrentStep,
    nextStep: state.nextStep,
    prevStep: state.prevStep,
  }));
export const useFormActions = () =>
  useValuationFormStore((state) => ({
    updateFormData: state.updateFormData,
    updateStep1Data: state.updateStep1Data,
    updateStep2Data: state.updateStep2Data,
    updateStep3Data: state.updateStep3Data,
    resetForm: state.resetForm,
    isStepValid: state.isStepValid,
    getStepData: state.getStepData,
  }));